import { createClient } from '@supabase/supabase-js';

// ----------------------------------------------------------------
// Database types
// ----------------------------------------------------------------

export type UserRole = 'admin' | 'unos' | 'backoffice' | 'savjetnik';
export type ObracunStatus = 'processing' | 'completed' | 'error';
export type InputFajlTip = 'file1_agproant' | 'file2_tripro' | 'file3_uizv' | 'obracun_template';

/** Dozvole žive na profilu; uloga je samo predložak s kojim se nalog otvara. */
export const DOZVOLE = [
  'moze_generisati',
  'moze_historija',
  'moze_obracun_mjeseca',
  'moze_postavke_uposlenika',
  'moze_uposlenike',
  'moze_opste_postavke',
  'vidi_opomene',
  'moze_slati_mail',
  'moze_korisnike',
] as const;

export type Dozvola = typeof DOZVOLE[number];

export interface Profile {
  id: string;
  ime: string;
  prezime: string;
  role: UserRole;
  sifra_savjetnika: string | null;
  aktivan: boolean;
  created_at: string;
  moze_generisati: boolean;
  moze_historija: boolean;
  moze_obracun_mjeseca: boolean;
  moze_postavke_uposlenika: boolean;
  moze_uposlenike: boolean;
  moze_opste_postavke: boolean;
  vidi_opomene: boolean;
  moze_slati_mail: boolean;
  moze_korisnike: boolean;
  /** Suzuje moze_uposlenike na jedan tip; null = bez ograničenja. */
  uposlenici_dozvoljen_tip: 'savjetnik' | 'call_centar' | 'administracija' | null;
}

export interface Obracun {
  id: string;
  period: string;           // format: '2026-03'
  created_by: string | null;
  created_at: string;
  status: ObracunStatus;
  error_msg: string | null;
}

export interface ObracunSavjetnik {
  id: string;
  obracun_id: string;
  savjetnik_id: string | null;
  sifra_savjetnika: string;
  ime_savjetnika: string;
  bbs_tekuca: number | null;
  bbs_portfeljna: number | null;
  bbs_storno: number | null;
  bbs_mirovanje: number | null;
  ukupno_bbs: number | null;
  neto_plata: number | null;
  ipl_ukupni: number | null;
  ipl_6mj: number | null;
  ipl_zadnji_mj: number | null;
  broj_novih_polica: number | null;
  npp_obrocna: number | null;
  storage_path_opp: string | null;
  storage_path_bbs: string | null;
  storage_path_word: string | null;
  created_at: string;
}

export interface InputFajl {
  id: string;
  obracun_id: string;
  tip: InputFajlTip;
  storage_path: string;
  originalni_naziv: string | null;
  created_at: string;
}

export interface Database {
  public: {
    Tables: {
      profiles: {
        Row: Profile;
        Insert: Omit<Profile, 'created_at'>;
        Update: Partial<Omit<Profile, 'id' | 'created_at'>>;
      };
      obracuni: {
        Row: Obracun;
        Insert: Omit<Obracun, 'id' | 'created_at'>;
        Update: Partial<Omit<Obracun, 'id' | 'created_at'>>;
      };
      obracun_savjetnici: {
        Row: ObracunSavjetnik;
        Insert: Omit<ObracunSavjetnik, 'id' | 'created_at'>;
        Update: Partial<Omit<ObracunSavjetnik, 'id' | 'created_at'>>;
      };
      input_fajlovi: {
        Row: InputFajl;
        Insert: Omit<InputFajl, 'id' | 'created_at'>;
        Update: Partial<Omit<InputFajl, 'id' | 'created_at'>>;
      };
    };
  };
}

// ----------------------------------------------------------------
// Klijent
// ----------------------------------------------------------------

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL!;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY!;

// Sesija se pamti u localStorage i obnavlja sama (refresh token), pa se korisnik
// ne prijavljuje svaki put — samo nakon izričite odjave ili isteka refresh tokena.
export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: false },
});

// ----------------------------------------------------------------
// Auth helpers
// ----------------------------------------------------------------

/**
 * Supabase-jev interni auth lock (navigator.locks) povremeno ostane zaglavljen
 * (spor network, tab u pozadini) i onda auth pozivi vise zauvijek — jedini
 * lijek bez ovog wrappera bio je refresh stranice. Ovaj timeout garantuje da
 * promise uvijek settle-uje, pa pozivaoc moze prikazati gresku umjesto da
 * korisnik ostane zaglavljen na spinneru/dugmetu.
 */
export const withTimeout = <T,>(promise: Promise<T>, ms: number, label: string): Promise<T> =>
  new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(`Timeout: ${label}`)), ms);
    promise.then(
      v => { clearTimeout(timer); resolve(v); },
      e => { clearTimeout(timer); reject(e); }
    );
  });

export const getCurrentUser = () => supabase.auth.getUser();

export const getCurrentSession = () => supabase.auth.getSession();

export const signIn = (email: string, password: string) =>
  supabase.auth.signInWithPassword({ email, password });

export const signOut = () => supabase.auth.signOut();

export const getProfile = async (userId: string): Promise<Profile | null> => {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', userId)
    .single();
  if (error) {
    console.error('getProfile:', error);
    return null;
  }
  return data;
};

// ----------------------------------------------------------------
// Storage helpers — download putem signed URL (kratko trajanje)
// ----------------------------------------------------------------

export const getSignedUrl = async (
  storagePath: string,
  expiresIn = 3600
): Promise<string | null> => {
  const { data, error } = await supabase.storage
    .from('triglav-obracuni')
    .createSignedUrl(storagePath, expiresIn);
  if (error) return null;
  return data.signedUrl;
};
